import React from 'react';
import {FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers} from "formik";
import {FactTemplate, FactTemplateData} from "types/FactTemplate";
import IconSelector from "components/Form/IconSelector";
import IconName from "types/IconName";
import Button from "components/Button";
import Field from "components/Form/Field";
import FactType from "types/FactType";
import Selector from "components/Form/Selector";
import {PlusIcon} from "@heroicons/react/solid";
import {ClipboardListIcon, TrashIcon} from "@heroicons/react/outline";
import {useOperator} from "context/OperatorContext";
import FormControls from "components/FormControls";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import {TextField} from "../../../components/Form/Form";

// Helpers ----

const factTypes = [
  {id: FactType.SHORT_TEXT, name: 'Short Text', description: "250 characters"},
  {id: FactType.MEDIUM_TEXT, name: 'Medium Text', description: "5000 characters"},
  {id: FactType.LONG_TEXT, name: 'Long Text', description: "50000 characters"},
  {id: FactType.OPTION, name: 'Option', description: "Multiple choice"},
]

// Component ----

type FactTemplateFormProps = {
  factTemplate?: FactTemplate | null
  onSubmit: (values: FactTemplateData, helpers: FormikHelpers<FactTemplateData>) => Promise<any>
}

function FactTemplateForm({factTemplate, onSubmit}: FactTemplateFormProps) {
  const {termFor} = useOperator();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(250, "Maximum character limit exceeded"),
  })

  const initialValues: FactTemplateData = {
    name: factTemplate?.name ?? '',
    icon: factTemplate?.icon ?? IconName.QUESTION_MARK_CIRCLE,
    type: factTemplate?.type ?? FactType.SHORT_TEXT,
    options: factTemplate?.type === FactType.OPTION ? factTemplate.options : []
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {formik => (
        <Form className="space-y-8 divide-y divide-gray-200">
          <ErrorNotification formik={formik} />
          
          <div className="shadow sm:rounded-md">
            <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
              <div className="space-y-8 divide-y divide-gray-200">
                <div>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Key Information
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Basic details about the {termFor('fact')}
                    </p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                    {/* Name */}
                    <div className="sm:col-span-3">
                      <div className="mt-1">
                        <TextField
                          label='Name *'
                          name="name"
                          id="name"
                          hint='250 chars max'
                          autoComplete="off"
                          maxLength={250}
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="sm:col-span-1 sm:col-start-1">
                      <IconSelector
                        selected={initialValues.icon}
                        onChange={(iconName) => formik.setFieldValue('icon', iconName)}
                      />
                    </div>

                    {/* Type */}
                    <div className="sm:col-span-3 xl:col-span-2">
                      <Selector
                        label="Type *"
                        items={factTypes}
                        selectedId={initialValues.type}
                        disabled={(factTemplate?.use_count ?? 0) > 0}
                        required
                        onChange={type => {
                          void formik.setFieldValue('type', type.id)
                          void formik.setFieldValue('options', type.id === FactType.OPTION ? [''] : [])
                        }}
                      />
                    </div>
                  </div>
                </div>

                {formik.values.type === FactType.OPTION &&
                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Options
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Specify multiple choice options for this {termFor('fact')}
                      </p>
                    </div>
                    <div className="mt-6">

                      {/* Options */}
                      <FieldArray name="options">
                        {({push, remove}: FieldArrayRenderProps) => (
                          <div>
                            <ul>
                              {formik.values.options?.map((option, index) => (
                                <li key={index} className="my-2">
                                  <div className="flex items-center justify-between space-x-1 lg:space-x-4">
                                    <div className="w-full">
                                      <label
                                        htmlFor={`options.${index}`}
                                        className="sr-only"
                                      >{`Option ${index + 1}`}</label>
                                      <Field
                                        type="text"
                                        icon={ClipboardListIcon}
                                        name={`options.${index}`}
                                        id={`options.${index}`}
                                        autoComplete="off"
                                        maxLength="255"
                                        required
                                        placeholder={`Option ${index + 1}`}
                                      />
                                    </div>
                                    <div className="flex-grow-0">
                                      <Button
                                        size={Button.sizes.TINY}
                                        color={Button.colors.WHITE}
                                        icon={TrashIcon}
                                        onClick={() => remove(index)}
                                        disabled={(formik.values.options?.length || 0) < 2}
                                      />
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>

                            <div className="mt-2">
                              <Button
                                color={Button.colors.SECONDARY}
                                icon={PlusIcon}
                                size={Button.sizes.TINY}
                                onClick={() => push('')}
                              >
                                Add Option
                              </Button>
                            </div>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                }
              </div>
            </div>

            <FormControls formik={formik}/>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default FactTemplateForm;